// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Chip, Group, Input, Radio, Select, SimpleGrid, Stack, Text, ThemeIcon } from '@mantine/core';
import type { JSX, ReactNode } from 'react';
import { useId, useState } from 'react';
import { IMaskInput } from 'react-imask';
import { useNavigate } from 'react-router';
import type { EligibilityAnswers, PaymentChoice } from './answers';
import { useIntakeV3Answers } from './answers';
import { ADULT_AGE, ageFromDateOfBirth, MINIMUM_AGE, OTHER_STATE, SERVED_STATES, UNSERVED_STATES } from './eligibility';
import { controlClasses, Note } from './IntakeV3Controls';
import { IntakeV3Frame } from './IntakeV3Frame';
import { AlertIcon, ArrowIcon, CardIcon, PinIcon, WalletIcon } from './icons';
import { Question } from './Question';
import type { IntakeV3StepProps } from './steps';
import { getIntakeV3StepPath, INTAKE_V3_WAITLISTS } from './steps';

const STATE_OPTIONS: { value: NonNullable<EligibilityAnswers['state']>; label: string }[] = [
  ...SERVED_STATES.map((state) => ({ value: state.code, label: state.label })),
  { value: OTHER_STATE, label: 'Another state' },
];

const PAYMENT_OPTIONS: { value: PaymentChoice; title: string; description: string; icon: ReactNode }[] = [
  {
    value: 'insurance',
    title: 'I have insurance',
    description: 'Plan and member ID on the next step.',
    icon: <CardIcon />,
  },
  {
    value: 'self-pay',
    title: "I'll pay myself",
    description: 'Flat visit price. No surprise bills.',
    icon: <WalletIcon />,
  },
];

function dateOfBirthHint(age: number | null): string | undefined {
  if (age === null) {
    return `Patients must be ${MINIMUM_AGE} or older to be seen at Polaris.`;
  }
  if (age >= MINIMUM_AGE && age < ADULT_AGE) {
    return `Ages ${MINIMUM_AGE}–${ADULT_AGE - 1}: a parent or legal guardian completes booking, consent, and the sleep questionnaire.`;
  }
  return age >= ADULT_AGE ? `Age ${age}` : undefined;
}

/**
 * First v3 step: where the visit happens, date of birth, and how the patient pays.
 * Each question appears once the one before it is answered. Out-of-area and
 * under-age patients leave the flow for a waitlist before being asked anything else.
 */
export function EligibilityStep({ step, onContinue }: IntakeV3StepProps): JSX.Element {
  const navigate = useNavigate();
  const { answers, updateAnswers } = useIntakeV3Answers();
  const { state, otherState, dateOfBirth, payment } = answers.eligibility;
  const [attempted, setAttempted] = useState(false);
  const dateOfBirthId = useId();

  const isOtherState = state === OTHER_STATE;
  const isServedState = !!state && !isOtherState;
  const age = ageFromDateOfBirth(dateOfBirth);
  const isUnderAge = age !== null && age < MINIMUM_AGE;
  const showPayment = isServedState && age !== null && !isUnderAge;
  const joinsWaitlist = isOtherState || (isServedState && isUnderAge);

  const dateOfBirthComplete = (dateOfBirth ?? '').length === 10;
  const dateOfBirthError =
    age === null && (attempted || dateOfBirthComplete) ? 'Enter a valid date of birth, MM/DD/YYYY' : null;

  const setDateOfBirth = (value: string): void => {
    const nextAge = ageFromDateOfBirth(value);
    updateAnswers('eligibility', {
      dateOfBirth: value,
      minor: nextAge !== null && nextAge >= MINIMUM_AGE && nextAge < ADULT_AGE,
    });
  };

  const handleContinue = (): void => {
    setAttempted(true);
    if (isOtherState) {
      if (otherState) {
        navigate(getIntakeV3StepPath(INTAKE_V3_WAITLISTS.outOfArea));
      }
      return;
    }
    if (!state || age === null) {
      return;
    }
    if (isUnderAge) {
      navigate(getIntakeV3StepPath(INTAKE_V3_WAITLISTS.underAge));
      return;
    }
    if (payment) {
      onContinue();
    }
  };

  return (
    <IntakeV3Frame
      currentSection={step.section}
      title={step.title}
      description="Three quick answers, before any appointment time is held."
      actions={
        <Button rightSection={joinsWaitlist ? undefined : <ArrowIcon />} onClick={handleContinue}>
          {joinsWaitlist ? 'Join the waitlist' : 'Continue'}
        </Button>
      }
    >
      <Stack gap={26}>
        <Question
          variant="section"
          label="What state will you be in for your visit?"
          error={attempted && !state ? 'Choose a state' : null}
        >
          <Chip.Group
            multiple={false}
            value={state ?? null}
            onChange={(value: string) =>
              updateAnswers('eligibility', { state: value as EligibilityAnswers['state'], otherState: undefined })
            }
          >
            <Group gap={8}>
              {STATE_OPTIONS.map((option) => (
                <Chip key={option.value} value={option.value} variant="outline">
                  {option.label}
                </Chip>
              ))}
            </Group>
          </Chip.Group>
          {isOtherState && (
            <Stack gap={12} mt={12}>
              <Select
                label="Which state?"
                placeholder="Choose your state"
                data={UNSERVED_STATES}
                searchable
                value={otherState ?? null}
                onChange={(value: string | null) => updateAnswers('eligibility', { otherState: value ?? undefined })}
                error={attempted && !otherState ? 'Choose a state' : undefined}
              />
              <Note tone="orange" icon={<PinIcon />}>
                We're not licensed there yet. Leave your name and email and we'll tell you the day we open.
              </Note>
            </Stack>
          )}
        </Question>

        {isServedState && (
          <Stack gap={10}>
            <Input.Wrapper
              id={dateOfBirthId}
              label="Date of birth"
              labelProps={{ className: controlClasses.section, 'data-section': true }}
              error={dateOfBirthError}
              description={dateOfBirthError ? undefined : dateOfBirthHint(age)}
              inputWrapperOrder={['label', 'input', 'description', 'error']}
            >
              <Input
                component={IMaskInput}
                mask="00/00/0000"
                placeholder="MM/DD/YYYY"
                inputMode="numeric"
                autoComplete="bday"
                id={dateOfBirthId}
                value={dateOfBirth ?? ''}
                onAccept={(value: string) => setDateOfBirth(value)}
                error={!!dateOfBirthError}
              />
            </Input.Wrapper>
            {isUnderAge && (
              <Note tone="orange" icon={<AlertIcon />}>
                We can't see patients under {MINIMUM_AGE} at this time. Continue to join our waitlist.
              </Note>
            )}
          </Stack>
        )}

        {showPayment && (
          <Question
            variant="section"
            label="How will you pay?"
            error={attempted && !payment ? "Choose how you'll pay" : null}
          >
            <Radio.Group
              value={payment ?? null}
              onChange={(value: string) => updateAnswers('eligibility', { payment: value as PaymentChoice })}
            >
              <SimpleGrid cols={{ base: 1, xs: 2 }} spacing={10}>
                {PAYMENT_OPTIONS.map((option) => (
                  <Radio.Card key={option.value} value={option.value} aria-label={option.title}>
                    <Group wrap="nowrap" align="flex-start" gap={13}>
                      <Radio.Indicator />
                      <ThemeIcon variant="light">{option.icon}</ThemeIcon>
                      <Stack gap={5}>
                        <Text fw={700} fz={15} lh={1.35} c="var(--ink-strong)">
                          {option.title}
                        </Text>
                        <Text fz={13} lh={1.55} c="var(--muted)">
                          {option.description}
                        </Text>
                      </Stack>
                    </Group>
                  </Radio.Card>
                ))}
              </SimpleGrid>
            </Radio.Group>
          </Question>
        )}
      </Stack>
    </IntakeV3Frame>
  );
}
